// KPI Builder — errors (block Save) and warnings (allowed, with an explanation and optional fix).
import {
  availableKpiFilters, chartOptions, chosenKpiFilters, codeFromTitle, isDataComplete, resolvedChart, selectedGroups, source, sourceB,
} from './engine';
import { ALL_GROUPS } from 'constants/kpiBuilderCatalogue';
import { advancedProblems } from './advanced';
import { BuilderAnswers, Issue } from './types';

export interface ValidationContext {
  title: string;
  roles: string[];
  dashboards: { id: string; audience: 'pre' | 'in' | 'any'; label: string }[];
  /** True when another visualization already uses this code. */
  codeTaken: boolean;
}

const PRE_ROLES = ['ROLE_ADMIN', 'STATE_PRE_SERVICE_OFFICER', 'DISTRICT_PRE_SERVICE_OFFICER', 'BLOCK_PRE_SERVICE_OFFICER', 'BNRC_STATE_OFFICER'];
const IN_ROLES = ['ROLE_ADMIN', 'STATE_IN_SERVICE_OFFICER', 'DISTRICT_IN_SERVICE_OFFICER', 'BLOCK_IN_SERVICE_OFFICER', 'BNRC_STATE_OFFICER'];

const isInServiceIds = (ids: string[]): boolean => ids.includes('inService') || ids.includes('inServiceAnm');

// The database stores titles in a Western-European character set: letters, digits, punctuation and
// the usual typographic marks are fine, while arrows, emoji and most other symbols are not.
const STORABLE = /^[\u0020-\u00FF\u2013\u2014\u2018\u2019\u201C\u201D\u2022\u2026\u20AC]*$/;
const hasUnsafeCharacters = (text: string): boolean => !STORABLE.test(text);

type Check = (a: BuilderAnswers, ctx: ValidationContext) => Issue[];

const infoIssues: Check = (_a, ctx) => {
  const out: Issue[] = [];
  const title = ctx.title.trim();
  if (!title) out.push({ tab: 'info', level: 'error', title: 'Title is missing.', msg: 'Add a title, or finish the Data tab to get a suggested one.' });
  if (title && ctx.codeTaken) {
    out.push({ tab: 'info', level: 'error', title: 'This code is already used.', msg: `Another visualization already has the code “${codeFromTitle(ctx.title)}”. Change the title slightly so a new code is created.` });
  }
  if (hasUnsafeCharacters(ctx.title)) {
    out.push({ tab: 'info', level: 'warning', title: 'Unusual characters in the title.', msg: 'Characters such as arrows or emoji may not be stored by the database and can make Saving fail. Use plain letters, numbers and punctuation.' });
  }
  if (ctx.title.length > 80) out.push({ tab: 'info', level: 'warning', title: 'Long title.', msg: 'Titles over 80 characters get cut off on dashboard cards. Try a shorter one and put details in the subtitle.' });
  return out;
};

const listIssues: Check = (a) => {
  if (a.recipe !== 'list' || !a.src || a.cols.includes('name')) return [];
  return [{ tab: 'data', level: 'warning', title: 'No Name column.', msg: 'Without it, dashboard users only see learner IDs, which mean nothing to them.', fix: { label: 'Add Name', answers: { cols: ['name', ...a.cols] } } }];
};

const gapIssues: Check = (a) => {
  const bothChosen = !!source(a) && !!sourceB(a);
  if (a.recipe !== 'gap' || selectedGroups(a).length > 0 || !bothChosen) return [];
  return [{ tab: 'data', level: 'warning', title: 'Everyone is included.', msg: `This compares all ${source(a)?.word} learners, pre-service and in-service together. Most existing KPIs of this type pick one of the two.` }];
};

const facilityIssues: Check = (a) => {
  if (a.recipe !== 'perGroup' || a.group !== 'facility' || a.topN < 50) return [];
  return [{ tab: 'data', level: 'warning', title: 'Many facilities.', msg: 'A chart with this many facility bars is hard to read. Top 10 or 20 is usually clearer.', fix: { label: 'Use top 10', answers: { topN: 10 } } }];
};

const unfinishedMessage = (a: BuilderAnswers): string => (a.recipe === 'advanced' ? advancedProblems(a.advanced).join(' ') : 'Answer all the questions in the Data tab.');

const unfinishedIssues: Check = (a) => (isDataComplete(a) ? [] : [{ tab: 'data', level: 'error', title: 'Data is not finished.', msg: unfinishedMessage(a) }]);

const dataIssues: Check = (a, ctx) => [unfinishedIssues, listIssues, gapIssues, facilityIssues].flatMap((check) => check(a, ctx));

const displayIssues: Check = (a) => {
  if (!isDataComplete(a) || !['donut', 'pie'].includes(resolvedChart(a) ?? '') || a.topN <= 6) return [];
  return [{ tab: 'display', level: 'warning', title: 'Too many slices.', msg: `A donut or pie with ${a.topN >= ALL_GROUPS ? 'every group as a slice' : `${a.topN} slices`} is hard to read. Use it for 6 or fewer groups.`, fix: { label: 'Switch to horizontal bars', answers: { chart: 'horizontalbar' } } }];
};

const chartFitIssues: Check = (a) => {
  const recommended = chartOptions(a)[0]?.id;
  if (!isDataComplete(a) || !recommended || resolvedChart(a) === recommended || chartOptions(a).some((c) => c.id === resolvedChart(a))) return [];
  return [{ tab: 'display', level: 'warning', title: 'Chart not made for this question.', msg: 'This chart may not show this data clearly. Check the dashboard after saving, or go back to the recommended chart.', fix: { label: 'Use the recommended chart', answers: { chart: null } } }];
};

const filterIssues: Check = (a) => {
  const kf = chosenKpiFilters(a);
  const courseOnly = kf.some((f) => f.id === 'course') && !kf.some((f) => f.id === 'competency');
  if (!courseOnly || !availableKpiFilters(a).some((f) => f.id === 'competency')) return [];
  return [{ tab: 'filters', level: 'warning', title: 'Course without Competency.', msg: 'The Course list is grouped by competency and is very long on its own.', fix: { label: 'Also add Competency', answers: { kpiFilters: [...a.kpiFilters, 'competency'] } } }];
};

const accessIssues: Check = (a, ctx) => {
  const out: Issue[] = [];
  const ids = selectedGroups(a).map((g) => g.id);
  if (ctx.roles.length === 0) out.push({ tab: 'access', level: 'error', title: 'Nobody can see this.', msg: 'Pick at least one role.', fix: { label: 'Admins only', roles: ['ROLE_ADMIN'] } });
  if (ids.includes('preService') && ctx.roles.some((r) => r.includes('IN_SERVICE'))) {
    out.push({ tab: 'access', level: 'warning', title: 'Audience mismatch.', msg: 'This KPI counts pre-service students, but in-service officer roles can see it.', fix: { label: 'Use Pre-Service officers', roles: PRE_ROLES } });
  }
  if (isInServiceIds(ids) && ctx.roles.some((r) => r.includes('PRE_SERVICE'))) {
    out.push({ tab: 'access', level: 'warning', title: 'Audience mismatch.', msg: 'This KPI counts in-service professionals, but pre-service officer roles can see it.', fix: { label: 'Use In-Service officers', roles: IN_ROLES } });
  }
  return out;
};

const dashboardIssues: Check = (a, ctx) => {
  const out: Issue[] = [];
  const ids = selectedGroups(a).map((g) => g.id);
  const isPre = ids.includes('preService');
  const isIn = isInServiceIds(ids);
  const mismatch = ctx.dashboards.filter((d) => (isPre && d.audience === 'in') || (isIn && d.audience === 'pre'));
  if (mismatch.length) {
    out.push({ tab: 'dashboards', level: 'warning', title: 'Audience mismatch.', msg: `This visualization counts ${isPre ? 'pre-service students' : 'in-service professionals'}, but ${mismatch.map((d) => d.label).join(', ')} is for the other audience. Check this is intended.` });
  }
  if (ctx.dashboards.length && ctx.roles.length === 1 && ctx.roles[0] === 'ROLE_ADMIN') {
    out.push({ tab: 'dashboards', level: 'warning', title: 'Only admins will see it.', msg: 'It is on a dashboard, but Access is set to Admins only, so officers will not see this chart.' });
  }
  return out;
};

const CHECKS: Check[] = [infoIssues, dataIssues, displayIssues, chartFitIssues, filterIssues, accessIssues, dashboardIssues];

export const validate = (a: BuilderAnswers, ctx: ValidationContext): Issue[] => CHECKS.flatMap((check) => check(a, ctx));

export const hasErrors = (issues: Issue[]): boolean => issues.some((i) => i.level === 'error');
